import { useEffect, useState, type ReactElement } from "react";

import { ArrowRight, MessageCircleQuestion } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "~/components/ui/dialog";
import { Textarea } from "~/components/ui/textarea";
import { trackEvent } from "~/lib/analytics";
import { cn } from "~/lib/utils";

const contactTriggerVariants = {
    amber: "text-amber-700/70 hover:text-amber-900",
    red: "text-red-700/70 hover:text-red-900",
    forest: "text-brand-forest-600/70 hover:text-brand-forest-800",
    neutral: "text-muted-foreground hover:text-foreground",
} as const;

interface PaywallContactDialogProps {
    surface: string;
    billingState?: string | null;
    variant?: "amber" | "red" | "forest" | "neutral";
    /** "question" renders MessageCircleQuestion left of text; "arrow-right" renders ArrowRight right of text */
    triggerIcon?: "question" | "arrow-right";
    className?: string;
    trigger?: ReactElement;
}

// AIDEV-NOTE: Self-contained dialog for paywall contact questions.
// Renders its own trigger so parent components just drop in <PaywallContactDialog />.
export function PaywallContactDialog({
    surface,
    billingState,
    variant = "neutral",
    triggerIcon = "question",
    className,
    trigger,
}: PaywallContactDialogProps) {
    const [open, setOpen] = useState(false);
    const [question, setQuestion] = useState("");
    const [status, setStatus] = useState<
        "idle" | "submitting" | "success" | "error"
    >("idle");

    async function handleSubmit() {
        if (!question.trim() || status === "submitting") return;

        setStatus("submitting");

        try {
            const response = await fetch("/api/support/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    question: question.trim(),
                    surface,
                    billingState: billingState ?? undefined,
                }),
            });

            if (!response.ok) {
                setStatus("error");
                return;
            }

            trackEvent("support_contact_submitted", { surface });
            setStatus("success");
        } catch {
            setStatus("error");
        }
    }

    // AIDEV-NOTE: Auto-close dialog 3s after successful submission.
    useEffect(() => {
        if (status !== "success") return;
        const timer = setTimeout(() => setOpen(false), 3000);
        return () => clearTimeout(timer);
    }, [status]);

    function handleOpenChange(nextOpen: boolean) {
        setOpen(nextOpen);
        if (!nextOpen) {
            // Reset form when closing
            setQuestion("");
            setStatus("idle");
        }
    }

    return (
        <Dialog
            analyticsId="PaywallContactDialog"
            open={open}
            onOpenChange={handleOpenChange}
        >
            <DialogTrigger asChild>
                {trigger ?? (
                    <button
                        type="button"
                        className={cn(
                            "inline-flex items-center gap-1.5 text-xs underline-offset-2 hover:underline",
                            contactTriggerVariants[variant],
                            className,
                        )}
                    >
                        {triggerIcon === "question" && (
                            <MessageCircleQuestion className="size-3.5" />
                        )}
                        Have any questions?
                        {triggerIcon === "arrow-right" && (
                            <ArrowRight className="size-3" />
                        )}
                    </button>
                )}
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Ask us a question</DialogTitle>
                    <DialogDescription>
                        We&apos;ll get back to you as soon as possible.
                    </DialogDescription>
                </DialogHeader>

                {status === "success" ? (
                    <div className="py-4 text-center">
                        <p className="text-sm font-medium text-brand-forest-700">
                            Thanks for reaching out!
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                            We&apos;ll get back to you shortly.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <Textarea
                            placeholder="What would you like to know?"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            maxLength={2000}
                            rows={4}
                            disabled={status === "submitting"}
                        />
                        {status === "error" && (
                            <p className="text-sm text-red-600">
                                Something went wrong. Please try again.
                            </p>
                        )}
                        <div className="flex justify-end">
                            <Button
                                onClick={handleSubmit}
                                disabled={
                                    !question.trim() || status === "submitting"
                                }
                            >
                                {status === "submitting"
                                    ? "Sending..."
                                    : "Send"}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
